/**
 * Disconnected Overlay
 *
 * Full-screen blocking overlay displayed when the SATCHEL API server
 * is not available. Non-dismissible - the app cannot function without
 * the backend server.
 *
 * Features:
 * - Full-screen blocking (z-index above everything)
 * - Retry button for manual reconnection attempts
 * - Electron-specific: Shows embedded server status and controls
 * - Technical details (collapsed by default)
 */

import React, { useState } from "react";
import { ASSET_PATHS } from "@satchel/core";
import { isElectron } from "../utils/runtime";
import { useServerConnection, type EmbeddedServerStatus } from "../context/ServerConnectionContext";

/**
 * Format a date for display
 */
const formatTime = (date: Date | null): string => {
  if (!date) return "Never";
  return date.toLocaleTimeString();
};

/**
 * Electron Server Controls
 * Shows embedded server status and restart button
 */
const ElectronServerControls: React.FC<{
  embeddedServer: EmbeddedServerStatus | null;
}> = ({ embeddedServer }) => {
  const [isRestarting, setIsRestarting] = useState(false);
  const [restartError, setRestartError] = useState<string | null>(null);

  const handleRestart = async () => {
    if (!window.electronAPI?.restartServer) return;

    setIsRestarting(true);
    setRestartError(null);

    try {
      const result = await window.electronAPI.restartServer();
      if (!result.success) {
        setRestartError(result.error || "Failed to restart server");
      }
    } catch (error) {
      setRestartError(error instanceof Error ? error.message : "Unknown error");
    } finally {
      setIsRestarting(false);
    }
  };

  const handleStart = async () => {
    if (!window.electronAPI?.startServer) return;

    setIsRestarting(true);
    setRestartError(null);

    try {
      const result = await window.electronAPI.startServer();
      if (!result.success) {
        setRestartError(result.error || "Failed to start server");
      }
    } catch (error) {
      setRestartError(error instanceof Error ? error.message : "Unknown error");
    } finally {
      setIsRestarting(false);
    }
  };

  if (!embeddedServer) return null;

  // Binary is missing. In a packaged release this is a packaging/CI defect, not
  // something the end user can fix by compiling Rust; show dev setup steps only
  // in development builds.
  if (embeddedServer.binaryMissing) {
    return (
      <div className="mt-6 p-4 bg-sa-warning/10 border border-sa-warning/40 rounded-lg">
        <h4 className="text-sa-warning font-medium mb-2">Server Binary Not Found</h4>
        {import.meta.env.PROD ? (
          <>
            <p className="text-sm text-sa-text mb-3">
              This build was packaged without its embedded server binary. This is a
              packaging/release defect, not a problem with your machine.
            </p>
            <p className="text-sm text-sa-text-dim mb-1">
              Please reinstall a fixed release, or report this with the details below:
            </p>
            {embeddedServer.error && (
              <p className="text-sm text-sa-danger mt-2 break-all">{embeddedServer.error}</p>
            )}
          </>
        ) : (
          <>
            <p className="text-sm text-sa-text mb-3">
              The SATCHEL server binary is not installed. To set up:
            </p>
            <ol className="text-sm text-sa-text-dim list-decimal list-inside space-y-1 mb-4">
              <li>
                Fetch the binaries:{" "}
                <code className="bg-sa-surface px-1.5 py-0.5 rounded text-xs">
                  pnpm --filter @satchel/electron fetch-binaries
                </code>
              </li>
              <li>Restart the application</li>
            </ol>
          </>
        )}
      </div>
    );
  }

  // Server is not running
  if (!embeddedServer.running) {
    return (
      <div className="mt-6 p-4 bg-sa-surface-hover border border-sa-border rounded-lg">
        <h4 className="text-sa-text font-medium mb-2">Embedded Server</h4>
        <p className="text-sm text-sa-text-dim mb-3">
          Status: <span className="text-sa-danger">Not running</span>
        </p>
        {embeddedServer.error && (
          <p className="text-sm text-sa-danger mb-3">{embeddedServer.error}</p>
        )}
        <button
          onClick={handleStart}
          disabled={isRestarting}
          className="px-4 py-2 bg-primary hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
        >
          {isRestarting ? "Starting..." : "Start Server"}
        </button>
        {restartError && <p className="mt-2 text-sm text-sa-danger">{restartError}</p>}
      </div>
    );
  }

  // Server is running but not responding
  return (
    <div className="mt-6 p-4 bg-sa-surface-hover border border-sa-border rounded-lg">
      <h4 className="text-sa-text font-medium mb-2">Embedded Server</h4>
      <p className="text-sm text-sa-text-dim mb-1">
        Status: <span className="text-sa-warning">Running but not responding</span>
      </p>
      <p className="text-sm text-sa-text-muted mb-3">PID: {embeddedServer.pid}</p>
      <button
        onClick={handleRestart}
        disabled={isRestarting}
        className="px-4 py-2 bg-primary hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-full text-sm font-medium transition-colors"
      >
        {isRestarting ? "Restarting..." : "Restart Server"}
      </button>
      {restartError && <p className="mt-2 text-sm text-sa-danger">{restartError}</p>}
    </div>
  );
};

/**
 * Technical Details Section (collapsible)
 */
const TechnicalDetails: React.FC<{
  lastError: string | null;
  lastConnected: Date | null;
  lastChecked: Date | null;
  latency: number | null;
  apiUrl: string;
  failureCount: number;
}> = ({ lastError, lastConnected, lastChecked, latency, apiUrl, failureCount }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="mt-6">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 text-sm text-sa-text-dim hover:text-sa-text transition-colors"
      >
        <svg
          className={`w-4 h-4 transition-transform ${isExpanded ? "rotate-90" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        Technical Details
      </button>

      {isExpanded && (
        <div className="mt-3 p-4 bg-sa-surface-hover border border-sa-border rounded-lg text-sm font-mono">
          <div className="space-y-2 text-sa-text-dim">
            <div className="flex justify-between">
              <span>API URL:</span>
              <span className="text-sa-text">{apiUrl}</span>
            </div>
            <div className="flex justify-between">
              <span>Last Error:</span>
              <span className="text-sa-danger">{lastError || "None"}</span>
            </div>
            <div className="flex justify-between">
              <span>Last Connected:</span>
              <span className="text-sa-text">{formatTime(lastConnected)}</span>
            </div>
            <div className="flex justify-between">
              <span>Last Check:</span>
              <span className="text-sa-text">{formatTime(lastChecked)}</span>
            </div>
            <div className="flex justify-between">
              <span>Latency:</span>
              <span className="text-sa-text">{latency !== null ? `${latency}ms` : "N/A"}</span>
            </div>
            <div className="flex justify-between">
              <span>Consecutive Failures:</span>
              <span className="text-sa-warning">{failureCount}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Disconnected Overlay Component
 */
export const DisconnectedOverlay: React.FC = () => {
  const {
    lastError,
    lastConnected,
    lastChecked,
    latency,
    apiUrl,
    failureCount,
    embeddedServer,
    checkNow,
    isChecking,
  } = useServerConnection();

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-sa-bg/95 backdrop-blur-sm"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="disconnect-title"
      aria-describedby="disconnect-description"
    >
      <div className="max-w-md w-full mx-4 text-center">
        {/* Logo */}
        <div className="mb-6">
          <img
            src={ASSET_PATHS.SATCHEL_LOGO}
            alt="SATCHEL"
            className="h-20 w-20 mx-auto rounded-lg opacity-50"
          />
        </div>

        {/* Disconnection Icon */}
        <div className="mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-sa-danger/10 border-2 border-sa-danger/40">
            <svg
              className="w-8 h-8 text-sa-danger"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M18.364 5.636a9 9 0 010 12.728m-3.536-3.536a5 5 0 000-7.072M13 12a1 1 0 11-2 0 1 1 0 012 0zm-9.364 5.636a9 9 0 010-12.728m3.536 3.536a5 5 0 000 7.072"
              />
              {/* Strike-through line */}
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4l16 16"
                className="text-sa-danger"
              />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h2 id="disconnect-title" className="text-2xl font-bold text-white mb-2">
          Cannot Connect to Server
        </h2>

        {/* Description */}
        <p id="disconnect-description" className="text-sa-text-dim mb-6">
          SATCHEL cannot function without a connection to the API server. Please ensure the server
          is running and try again.
        </p>

        {/* Retry Button */}
        <button
          onClick={checkNow}
          disabled={isChecking}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors min-w-[140px]"
        >
          {isChecking ? (
            <>
              <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Checking...
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Retry Connection
            </>
          )}
        </button>

        {/* Electron-specific controls */}
        {isElectron && <ElectronServerControls embeddedServer={embeddedServer} />}

        {/* Technical Details */}
        <TechnicalDetails
          lastError={lastError}
          lastConnected={lastConnected}
          lastChecked={lastChecked}
          latency={latency}
          apiUrl={apiUrl}
          failureCount={failureCount}
        />
      </div>
    </div>
  );
};

export default DisconnectedOverlay;
